import React from 'react'
import { themeColor, themeColorBlue, white } from '@edulastic/colors'
import { EduButton } from '@edulastic/common'
import styled, { css } from 'styled-components'
import {
  IconAudioPause,
  IconExclamationMark,
  IconPlayBig,
  IconPlayFilled,
  IconStop,
  IconStopCircle,
} from '@edulastic/icons'
import { Tooltip } from 'antd'

import { themes } from '../../theme'

const { playerSkin } = themes
const { defaultButton, navigationButtons } = playerSkin.sbac

const Controls = ({
  btnWithText,
  hideVisibility,
  className,
  padding,
  languageDropdownSelector,
  isSupported,
  loading,
  handlePlayPauseAudio,
  handleStopAudio,
  playPauseToolTip,
  play,
  pause,
  stop,
  isPremiumContentWithoutAccess,
  isMinimalDesign,
  playerSkinType,
  currentPlayingDetails,
  qId,
  ttsNotSupported,
  t: i18Translate,
}) => {
  return (
    <AudioButtonsWrapper
      btnWithText={btnWithText}
      hideVisibility={hideVisibility}
      className={className}
      padding={padding}
    >
      {languageDropdownSelector}
      {isSupported ? (
        <>
          <Tooltip
            title={!btnWithText ? playPauseToolTip : ''}
            id="play-pause-tooltip"
          >
            <AudioButton
              loading={loading}
              height="40px"
              ml="0px"
              IconBtn={!btnWithText}
              onClick={handlePlayPauseAudio}
              playerSkinType={playerSkinType}
              disabled={isPremiumContentWithoutAccess}
              data-cy="playBtn"
              isMinimalDesign={isMinimalDesign}
              id="play-pause-button"
              aria-describedby="play-pause-tooltip"
            >
              {currentPlayingDetails.qId === qId ? (
                <>
                  {btnWithText ? (
                    <>
                      <IconAudioPause />{' '}
                      {pause || i18Translate('common.test.ttsButton.pause')}
                    </>
                  ) : (
                    <IconAudioPause color={white} className="audio-pause" />
                  )}
                </>
              ) : (
                <>
                  {btnWithText ? (
                    <>
                      <IconPlayBig />{' '}
                      {play || i18Translate('common.test.ttsButton.play')}
                    </>
                  ) : (
                    <IconPlayFilled color={white} className="audio-play" />
                  )}
                </>
              )}
            </AudioButton>
          </Tooltip>
          <Tooltip
            title={
              !btnWithText
                ? stop || i18Translate('common.test.ttsButton.stop')
                : ''
            }
            id="stop-tooltip"
          >
            <AudioButton
              loading={loading}
              height="40px"
              IconBtn={!btnWithText}
              onClick={handleStopAudio}
              playerSkinType={playerSkinType}
              disabled={
                currentPlayingDetails.qId !== qId ||
                isPremiumContentWithoutAccess
              }
              data-cy="stopBtn"
              isMinimalDesign={isMinimalDesign}
              id="stop-button"
              aria-describedby="stop-tooltip"
            >
              <>
                {btnWithText ? (
                  <>
                    <IconStopCircle />{' '}
                    {stop || i18Translate('common.test.ttsButton.stop')}
                  </>
                ) : (
                  <IconStop color={white} className="audio-stop" />
                )}
              </>
            </AudioButton>
          </Tooltip>
        </>
      ) : (
        <Tooltip
          title={
            ttsNotSupported ||
            i18Translate('common.test.ttsButton.ttsNotSupported')
          }
          id="tts-not-supported-tooltip"
        >
          <AudioButton
            height="40px"
            IconBtn={!btnWithText}
            className="not-supported"
            playerSkinType={playerSkinType}
            disabled
            isMinimalDesign={isMinimalDesign}
            id="tts-not-supported-button"
            aria-describedby="tts-not-supported-tooltip"
          >
            <IconExclamationMark width={20} height={20} color={white} />
          </AudioButton>
        </Tooltip>
      )}
    </AudioButtonsWrapper>
  )
}

export default Controls

const AudioButtonsWrapper = styled.div`
  top: 0px;
  padding: ${({ padding }) => padding || '8px 16px'};
  ${({ btnWithText, hideVisibility }) => {
    const visibility = hideVisibility ? 'hidden' : 'visible'
    const display = btnWithText ? 'flex' : 'block'
    return css`
      display: ${display};
      visibility: ${visibility};
    `
  }}
`

const buttonStyles = {
  sbac: css`
    &.ant-btn.ant-btn-primary {
      background: ${navigationButtons.background};
      color: ${navigationButtons.color};
      border: 1px solid ${navigationButtons.color};

      svg {
        fill: ${navigationButtons.color};
      }

      &:hover {
        border: 1px solid ${defaultButton.hover.background};
        color: ${defaultButton.hover.color};
        background: ${defaultButton.hover.background};

        svg {
          fill: ${defaultButton.hover.color};
        }
      }
      &:active,
      &:focus {
        outline: 0;
        box-shadow: none !important;
        background: ${defaultButton.hover.background};
      }
    }
  `,
  minimal: css`
    &.ant-btn.ant-btn-primary {
      background: ${white};
      color: ${themeColor};
      border: none;
      box-shadow: none !important;
      font-size: 12px;

      svg {
        fill: ${themeColor};
        width: 20px;
        height: 20px;
      }

      &:hover,
      &:active,
      &:focus {
        box-shadow: none !important;
        outline: 0;
      }

      &:hover svg,
      &:active svg,
      &:focus svg {
        fill: ${white} !important;
      }
    }
  `,
}

const AudioButton = styled(EduButton)`
  position: relative;
  z-index: 998;

  &.ant-btn.ant-btn-primary.not-supported {
    background: white;
    border: 1px solid #de0b83;
  }
  &:focus {
    border: none;
    outline: 0;
    box-shadow: 0 0 0 2px ${themeColorBlue};
  }

  ${({ playerSkinType, isMinimalDesign }) =>
    buttonStyles[isMinimalDesign ? 'minimal' : playerSkinType]}
`
